import type { IExploreCountry, IExploreRegion } from '@atlas/types'

// Pure selectors over the explore read model. Geography is a FILTER here, not a
// destination: the page always lists brands, and region/country only narrow which
// markets those brands are counted over. Keeping this as free functions means the
// narrowing rules are unit-testable without rendering or a live API.

/** A brand rolled up across every market currently in scope. */
export interface BrandResult {
	brandId: string
	code: string
	name: string
	/** Every name the brand goes by anywhere, its own included — what search matches. */
	aliases: string[]
	/**
	 * DISTINCT assets across the in-scope markets, as the server counted them over the whole set.
	 *
	 * NOT a sum of the per-market counts. Counting is coverage-based, so one file tagged `emea`
	 * answers `1` for every EMEA market, and adding those up gives assets×markets — which is what
	 * made 2 Allflex files read "34 assets". Only the server sees the union, so the total is taken
	 * from the payload rather than derived here.
	 */
	assetCount: number
	/** Labels of the in-scope markets carrying the brand, most assets first. */
	markets: string[]
}

/** Geography option as the filter bar consumes it. `brandCount: 0` ⇒ nothing to browse. */
export interface GeoOption {
	code: string
	label: string
	brandCount: number
}

/**
 * The region owning a country code, or `null` if none does. Lets a hand-written or
 * shared `?country=` URL resolve its region instead of silently showing nothing.
 */
export function regionOfCountry(
	countriesByRegion: Record<string, IExploreCountry[]>,
	countryCode: string,
): string | null {
	const entry = Object.entries(countriesByRegion).find(([, countries]) => countries.some((c) => c.code === countryCode))
	return entry ? entry[0] : null
}

/**
 * The markets brands are counted over: one region's countries, or every region's when
 * no region is chosen, narrowed to a single country when one is.
 */
export function scopedCountries(
	regions: IExploreRegion[],
	countriesByRegion: Record<string, IExploreCountry[]>,
	regionCode: string | null,
	countryCode: string | null,
): IExploreCountry[] {
	const regionCodes = regionCode ? [regionCode] : regions.map((r) => r.code)
	const countries = regionCodes.flatMap((code) => countriesByRegion[code] ?? [])
	return countryCode ? countries.filter((c) => c.code === countryCode) : countries
}

/**
 * Brands in the scoped markets, deduped by brand: one brand sold in several markets is
 * ONE result whose `assetCount` is its DISTINCT assets across them and whose `markets` names them.
 * Ordered by assets desc then name, so the markets' biggest brands lead.
 *
 * The count is READ from the payload, never accumulated. Summing the per-market counts is what
 * made a brand's card claim more assets than the brand owns — see `BrandResult.assetCount`. The
 * markets list is still accumulated, because that genuinely is one entry per market.
 */
export function scopedBrands(countries: IExploreCountry[], totalsByBrand: Record<string, number> = {}): BrandResult[] {
	const byId = new Map<string, BrandResult>()
	for (const country of countries) {
		for (const brand of country.brands) {
			const seen = byId.get(brand.brandId)
			if (seen) {
				seen.markets.push(country.label)
			} else {
				// The scope-wide total when the caller has one, else the total on the row. The row's is
				// only ever right for a single-region scope, because the funnel counts per region.
				const total = totalsByBrand[brand.brandId] ?? brand.totalAssetCount
				byId.set(brand.brandId, { ...brand, assetCount: total, markets: [country.label] })
			}
		}
	}
	return [...byId.values()].sort((a, b) => b.assetCount - a.assetCount || a.name.localeCompare(b.name))
}

/**
 * Free-text narrowing on a brand's code and EVERY name it goes by. An empty needle matches all.
 *
 * Aliases, not just the displayed name: the same brand is Banamine in the US and Finadyne in EMEA,
 * and someone searching either should find it whichever market they happen to be browsing.
 * Matching only the market-resolved name would make a brand unfindable under the name half the
 * world knows it by.
 */
export function matchBrands(brands: BrandResult[], query: string): BrandResult[] {
	const needle = query.trim().toLowerCase()
	if (needle === '') return brands
	return brands.filter((b) => [b.code, ...b.aliases].join(' ').toLowerCase().includes(needle))
}

/** Region pills: the read model's own rollups, so an empty region renders inert. */
export function regionOptions(regions: IExploreRegion[]): GeoOption[] {
	return regions.map((r) => ({ code: r.code, label: r.label, brandCount: r.brandCount }))
}

/** Country pills for the active region - empty when no region is chosen. */
export function countryOptions(
	countriesByRegion: Record<string, IExploreCountry[]>,
	regionCode: string | null,
): GeoOption[] {
	if (!regionCode) return []
	return (countriesByRegion[regionCode] ?? []).map((c) => ({
		code: c.code,
		label: c.label,
		brandCount: c.brandCount,
	}))
}

/** Monogram for a brand with no logo: its name's first two alphanumerics. */
export function brandInitials(name: string): string {
	return (name.match(/[a-z0-9]/gi) ?? []).slice(0, 2).join('').toUpperCase()
}

/**
 * The market list a card shows. Long tails are truncated to a `+N` so a brand sold in
 * thirty countries does not outgrow its card.
 */
export function marketSummary(markets: string[], max = 3): string {
	if (markets.length <= max) return markets.join(' · ')
	return `${markets.slice(0, max).join(' · ')} +${markets.length - max}`
}
